import { useState } from "react";
import { put } from "../../common/api/apiShared";
import { DotLegalSelectOption } from "../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { getEnumValues } from "../../common/enumOperations";
import { useExpandedId } from "../../common/hooks/useExpandedId";
import { useOptimisticUpdate } from "../../common/hooks/useOptimisticUpdate";
import { useTranslation } from "../../localization/useTranslation";
import { Permission, PermissionLevel, PermissionType } from "../Roles.types";
import { IRolePermissionsDialogProps } from "./RolePermissionDialog";
import { PermissionListType, PermissionTypeContent, RolesSaveModel } from "./RolePermissionDialog.types";
import { useFeatureFlags, usePlanContext } from "../../auth/planProvider/PlanProvider";
import { FeatureFlag } from "../../auth/planProvider/PlanProvider.types";
import { useDotLegalMutation, useDotLegalQueryClient, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { TemplateEntityType } from "../../entityTemplates/EntityTemplate.types.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { getHiddenNavigationMenuItemsInPermissionArea, navigationMenuItemDefinitions } from "../../navigationMenuSettings/navigationMenuItems";

export function useRolePermissionDialogHooks(props: IRolePermissionsDialogProps) {
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const queryClient = useDotLegalQueryClient();
    const { translateString, translatePermissionTypes, translatePermissionLevel, translatePermissionListTypes, translatepermissionTypeToolTips } =
        useTranslation();
    const { hiddenNavigationMenuItems } = useUserContext();
    const { getExpandedId, setExpandedId } = useExpandedId();
    const [searchInput, setSearchInput] = useState("");
    const { addonsFeatures, informationSecurityFeatures, vendorManagementFeatures, platformFeatures, contractManagementFeatures } = usePlanContext();
    const featureFlags = useFeatureFlags();

    const key = `/roles/${props.id}/permissions`;
    const { isLoading, data } = useDotLegalQueryWithKey<RolesSaveModel>(key, `/roles/${props.id}/permissions`);
    const isStandardRole = data?.isStandardRole;

    const savePermissionMutation = useDotLegalMutation(savePermission);
    const removePermissionMutation = useDotLegalMutation(removePermission);

    let chosenUserRoles;
    let options = new Array<DotLegalSelectOption>();
    if (data) {
        chosenUserRoles = data.permissions
            .map((permissionContent) => {
                let typeContent = getPermissionContent().find((x) => x.masterPermission.type === permissionContent.type);
                if (typeContent) {
                    return {
                        masterPermission: permissionContent,
                        subPermissions: typeContent!.subPermissions
                            ? typeContent!.subPermissions.map((sub) => {
                                  return sub;
                              })
                            : undefined,
                        type: typeContent.type,
                        accessLevel: typeContent.accessLevel,
                        order: typeContent.order,
                        associatedPermissions: typeContent!.associatedPermissions,
                        disableAccessLevels: typeContent.disableAccessLevels,
                    };
                }
                return undefined;
            })
            .filter((n) => n !== undefined);
        PermissionLevelOptions();
    }

    async function ToggleSelectedMasterPermissionUserRoles(content: PermissionTypeContent) {
        let temp = { ...data! };

        let index = temp.permissions.findIndex((x) => x.type === content.masterPermission.type);

        if (index < 0) {
            temp.permissions.push({ type: content.masterPermission.type, level: content.masterPermission.level });

            content.subPermissions
                ?.filter((x) => subPermissionsThatShouldNotBeIncludedWhenPermissionIsSelected(x))
                .forEach((x) => {
                    temp.permissions.push({ type: x, level: null });
                });

            // Shown immediately rather than waited for: the server implies these from the flag and returns them on
            // the next read, so pushing them here only saves the area from flickering unselected until it lands.
            getPermissionsImpliedByFullEntityAccess(content.masterPermission.type)
                .filter((implied) => !temp.permissions.some((t) => t.type === implied.type))
                .forEach((implied) => temp.permissions.push(implied));

            await putOnQueueAndSetQueryData(temp, key, savePermissionMutation.mutateAsync, {
                type: content.masterPermission.type,
                level: content.masterPermission.level,
                subPermissions: content.subPermissions?.filter((x) => subPermissionsThatShouldNotBeIncludedWhenPermissionIsSelected(x)),
            });
            // Full entity access carries its area's permissions at max level, granted server side. Auto-adding them
            // here would push the content default instead, downgrading what the backend just set.
            getPermissionContent().forEach((p) => {
                if (
                    p.associatedPermissions &&
                    p.associatedPermissions.includes(content.masterPermission.type) &&
                    !isFullEntityAccessPermission(content.masterPermission.type) &&
                    !temp.permissions.some((t) => t.type === p.masterPermission.type)
                ) {
                    let associatedTempPermission = { type: p.masterPermission.type, level: p.masterPermission.level };
                    temp.permissions.push(associatedTempPermission);
                    putOnQueueAndSetQueryData(temp, key, savePermissionMutation.mutateAsync, associatedTempPermission);
                }
            });

            setExpandedId(content.masterPermission.type.toString());
        } else {
            var subPermissions = content.subPermissions ? temp.permissions.filter((x) => content.subPermissions!.some((y) => y === x.type)) : undefined;
            if (subPermissions && subPermissions.length > 0) {
                subPermissions.forEach((x) => {
                    let subPermissionIndex = temp.permissions.findIndex((y) => y.type === x.type);
                    temp.permissions.splice(subPermissionIndex, 1);
                    putOnQueueAndSetQueryData(temp, key, removePermissionMutation.mutateAsync, x.type);
                });
            }
            temp.permissions.splice(index, 1);

            await putOnQueueAndSetQueryData(temp, key, removePermissionMutation.mutateAsync, content.masterPermission.type);

            setExpandedId("");
        }

        // Revoking the flag revokes only the flag, so whatever the role configured for the area itself resurfaces.
        // Only the server knows what that is: the implied permissions pushed above were never stored.
        if (isFullEntityAccessPermission(content.masterPermission.type)) {
            await refetchPermissions();
        }
    }

    async function refetchPermissions() {
        await queryClient.refetchQueriesByPredicate((query) => query.queryKey.some((k) => typeof k === "string" && k.includes(key)));
    }

    async function setMasterPermissionLevel(permissionLevel: PermissionLevel, masterPermission: PermissionType, isSelected: boolean) {
        let temp = { ...data! };
        let tempMasterPermission = temp.permissions.find((x) => x.type === masterPermission);

        var level = isSelected ? permissionLevel : PermissionLevel.Read;

        tempMasterPermission!.level = level;

        await putOnQueueAndSetQueryData(temp, key, savePermissionMutation.mutateAsync, { type: tempMasterPermission!.type, level: level });

        await queryClient.refetchQueriesByPredicate((query) =>
            query.queryKey.some((k) => typeof k === "string" && k.includes(`/roles/${props.id}/field-permissions`))
        );
    }

    async function setSubPermission(content: PermissionTypeContent, chosenSubPermission: Array<string>) {
        let temp = { ...data! };

        content.subPermissions!.forEach((x) => {
            if (!temp.permissions.some((p) => p.type === x) && chosenSubPermission.some((s) => s === x.toString())) {
                temp.permissions.push({ type: x, level: null });
                putOnQueueAndSetQueryData(temp, key, savePermissionMutation.mutateAsync, { type: x, level: null });
            } else if (temp.permissions.some((p) => p.type === x) && !chosenSubPermission.some((s) => s === x.toString())) {
                let subIndex = temp.permissions.findIndex((c) => c.type === x);
                temp.permissions.splice(subIndex, 1);
                putOnQueueAndSetQueryData(temp, key, removePermissionMutation.mutateAsync, x);
            }
        });
    }

    function getSubPermissionSelectOptions(subPermissions: Array<PermissionType>): Array<DotLegalSelectOption> {
        let subSelectOptions = new Array<DotLegalSelectOption>();

        subPermissions.forEach((sub) => {
            subSelectOptions.push({
                id: sub.toString(),
                name: translatePermissionTypes(sub.toString()),
                hoverText: shouldShowSubPermissionTooltip(sub) ? translatepermissionTypeToolTips(sub.toString()) : undefined,
            });
        });

        return subSelectOptions;
    }

    function GetSubPermissionChosenOptions(subPermissions: Array<PermissionType>) {
        let chosenObtions: Array<string> = [];

        subPermissions.forEach((x) => {
            if (data!.permissions.some((y) => y.type === x)) {
                chosenObtions.push(x.toString());
            }
        });

        return chosenObtions;
    }

    function PermissionLevelOptions() {
        getEnumValues(PermissionLevel).forEach((x) => {
            if (!options.some((p) => p.id === x.toString())) {
                const name = translatePermissionLevel(x.toString());
                options.push({ name: name, id: x.toString() });
            }
        });
    }

    function PermissionListTypeOptions(): Array<DotLegalSelectOption> {
        let options: Array<DotLegalSelectOption> = [];
        getEnumValues(PermissionListType).forEach((x) => {
            const name = translatePermissionListTypes(x.toString());
            options.push({ name: name, id: x.toString() });
        });
        return options;
    }

    const handleCollapseClick = (Id: string) => {
        if (Id) {
            if (getExpandedId() === Id) setExpandedId("");
            else setExpandedId(Id);
        }
    };

    function savePermission(permission: Permission) {
        return put<{}>(`/roles/${props.id}/savePermission`, permission);
    }

    function removePermission(type: PermissionType) {
        return put<{}>(`/roles/${props.id}/removePermission/${type}`, null);
    }

    const getSelectableValues = (listType: PermissionListType) => getPermissionContent().filter((x) => x.type === listType);

    // The area stays grantable, since its pages are still reachable through links, but the admin should know that
    // nobody will find it in the menu.
    function getHiddenFromMenuText(listType: PermissionListType) {
        const hiddenItems = getHiddenNavigationMenuItemsInPermissionArea(hiddenNavigationMenuItems, listType);
        if (hiddenItems.length === 0) {
            return undefined;
        }

        const menuItems = hiddenItems.map((item) => translateString(navigationMenuItemDefinitions[item].labelKey)).join(", ");
        return translateString("hiddenFromMenu", { menuItems });
    }

    const hasFullContractAccess = chosenUserRoles?.some((x) => x!.masterPermission.type === PermissionType.ContractFullAccess) ?? false;
    const hasFullSystemAccess = chosenUserRoles?.some((x) => x!.masterPermission.type === PermissionType.SystemFullAccess) ?? false;
    const hasFullIncidentLogAccess = chosenUserRoles?.some((x) => x!.masterPermission.type === PermissionType.IncidentLogFullAccess) ?? false;
    const hasFullProcessAccess = chosenUserRoles?.some((x) => x!.masterPermission.type === PermissionType.ProcessFullAccess) ?? false;

    const isLockedByFullEntityAccess = (permissionType: PermissionType) => {
        if (permissionType === PermissionType.ContractsOverview || permissionType === PermissionType.Contracts) return hasFullContractAccess;
        if (permissionType === PermissionType.SystemManagement) return hasFullSystemAccess;
        if (permissionType === PermissionType.IncidentLog) return hasFullIncidentLogAccess;
        if (permissionType === PermissionType.Process) return hasFullProcessAccess;
        return false;
    };

    const chosenMasterPermissions = chosenUserRoles?.map((x) => x!.masterPermission) ?? [];

    // Full access locks the editors because the restrictions they edit are overridden while it is on: still stored,
    // but with no effect until it is revoked.
    const templatePermissionSections = [
        {
            listType: PermissionListType.Systems,
            managementPermission: PermissionType.SystemManagement,
            templateEntityType: TemplateEntityType.System,
            hasFullAccess: hasFullSystemAccess,
            readOnlyWarningKey: "systemManagementReadOnlyWarning",
        },
        {
            listType: PermissionListType.Contracts,
            managementPermission: PermissionType.Contracts,
            templateEntityType: TemplateEntityType.Contract,
            hasFullAccess: hasFullContractAccess,
            readOnlyWarningKey: "contractManagementReadOnlyWarning",
        },
    ]
        .filter((section) => chosenMasterPermissions.some((permission) => permission.type === section.managementPermission))
        .map((section) => ({
            listType: section.listType,
            templateEntityType: section.templateEntityType,
            editorsAreReadOnly: (isStandardRole ?? false) || section.hasFullAccess,
            showReadOnlyWarning:
                !section.hasFullAccess &&
                chosenMasterPermissions.some((permission) => permission.type === section.managementPermission && permission.level === PermissionLevel.Read),
            readOnlyWarningKey: section.readOnlyWarningKey,
        }));

    const getTemplatePermissionSection = (listTypeId: string) => templatePermissionSections.find((section) => section.listType.toString() === listTypeId);

    return {
        isLoading,
        data,
        chosenUserRoles,
        hasFullContractAccess,
        hasFullSystemAccess,
        hasFullIncidentLogAccess,
        hasFullProcessAccess,
        isLockedByFullEntityAccess,
        getTemplatePermissionSection,
        getPermissionContent,
        ToggleSelectedMasterPermissionUserRoles,
        handleCollapseClick,
        getExpandedId,
        setMasterPermissionLevel,
        setSubPermission,
        getSubPermissionSelectOptions,
        getSelectableValues,
        PermissionListTypeOptions,
        getHiddenFromMenuText,
        GetSubPermissionChosenOptions,
        searchInput,
        setSearchInput,
        isStandardRole,
    };

    function getPermissionContent(): Array<PermissionTypeContent> {
        let content: Array<PermissionTypeContent> = [
            {
                masterPermission: { type: PermissionType.ProcessingActivityManagement, level: PermissionLevel.Read },
                subPermissions: getProcessingActivitySubPermissions(),
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Delete],
                order: 1,
                associatedPermissions: [
                    PermissionType.ProcessingActivityMasterData,
                    PermissionType.ProcessingActivityDataSubject,
                    PermissionType.ProcessingActivityAssociation,
                    PermissionType.ProcessingActivityDataCategories,
                    PermissionType.ProcessingActivityLegalBases,
                    PermissionType.ProcessingActivitySystems,
                    PermissionType.ProcessingActivityDataProcessors,
                    PermissionType.ProcessingActivityDisclosures,
                    PermissionType.ProcessingActivitySources,
                    PermissionType.ProcessingActivityDataControllers,
                    PermissionType.CanValidateProcessingActivities,
                    PermissionType.ProcessingActivityJointDataControllers,
                    PermissionType.ProcessingActivityPolicies,
                ],
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivityMasterData, level: PermissionLevel.Read },
                subPermissions: [PermissionType.ProcessingActivityMasterDataPurpose],
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 3,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.UserManagement, level: PermissionLevel.Delete },
                subPermissions: undefined,
                type: PermissionListType.Settings,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 1,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            },
            {
                masterPermission: { type: PermissionType.CompanyManagement, level: PermissionLevel.Read },
                subPermissions: undefined,
                type: PermissionListType.LegalEntities,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 1,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.GroupCompanyManagement, level: PermissionLevel.Delete },
                subPermissions: undefined,
                type: PermissionListType.LegalEntities,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 2,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            },
            {
                masterPermission: { type: PermissionType.MasterDataManagement, level: PermissionLevel.Delete },
                subPermissions: undefined,
                type: PermissionListType.Settings,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 3,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            },
            {
                masterPermission: { type: PermissionType.RoleManagement, level: PermissionLevel.Delete },
                subPermissions: undefined,
                type: PermissionListType.Settings,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 4,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            },
            {
                masterPermission: { type: PermissionType.FullDataExport, level: PermissionLevel.Delete },
                subPermissions: undefined,
                type: PermissionListType.Settings,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 5,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            },
            {
                masterPermission: { type: PermissionType.CanPurchaseInPrivacy, level: null },
                subPermissions: undefined,
                type: PermissionListType.Settings,
                accessLevel: [],
                order: 7,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivityDataSubject, level: PermissionLevel.Read },
                subPermissions: [
                    PermissionType.ProcessingActivityDataSubjectNumberOfProcessors,
                    PermissionType.ProcessingActivityDataSubjectSecurityMeasures,
                    PermissionType.ProcessingActivityDataSubjectNumberOfDataSubjects,
                    PermissionType.ProcessingActivityDataSubjectDataSource,
                    PermissionType.ProcessingActivityDataSubjectNumberOfProcessorsOnlyCustomValue,
                    PermissionType.ProcessingActivityDataSubjectNumberOfDataSubjectsOnlyCustomValue,
                ],
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 4,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivityAssociation, level: PermissionLevel.Read },
                subPermissions: [
                    PermissionType.ProcessingActivityAssociationGroupEntities,
                    PermissionType.ProcessingActivityAssociationOwnership,
                    PermissionType.ProcessingActivityAssociationsBusinessAreas,
                ],
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 5,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivityDataCategories, level: PermissionLevel.Read },
                subPermissions: undefined,
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 6,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivityLegalBases, level: PermissionLevel.Read },
                subPermissions: [PermissionType.ProcessingActivityLegalBasisDeletionPeriods, PermissionType.ProcessingActivityLegalBasisLegalBases],
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 7,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivitySystems, level: PermissionLevel.Read },
                subPermissions: undefined,
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 8,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivityDataProcessors, level: PermissionLevel.Read },
                subPermissions: [
                    PermissionType.ProcessingActivityDataProcessorsDataProcessorAgreement,
                    PermissionType.ProcessingActivityDataProcessorsTiaAndTransferBasis,
                    PermissionType.ProcessingActivityDataProcessorsResponsible,
                    PermissionType.ProcessingActivityDataProcessorsSubProcessors,
                    PermissionType.ProcessingActivityDataProcessorsDataCategories,
                    PermissionType.ProcessingActivityDataProcessorsName,
                ],
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 9,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivityDisclosures, level: PermissionLevel.Read },
                subPermissions: [
                    PermissionType.ProcessingActivityDisclosuresAgreement,
                    PermissionType.ProcessingActivityDisclosuresTransferBasis,
                    PermissionType.ProcessingActivityDisclosuresBasisOfDisclosure,
                    PermissionType.ProcessingActivityDisclosuresResponsible,
                    PermissionType.ProcessingActivityDisclosuresDataCategories,
                    PermissionType.ProcessingActivityDisclosuresName,
                ],
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 10,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivitySources, level: PermissionLevel.Read },
                subPermissions: [
                    PermissionType.ProcessingActivitySourcesAgreement,
                    PermissionType.ProcessingActivitySourcesResponsible,
                    PermissionType.ProcessingActivitySourcesDataCategories,
                    PermissionType.ProcessingActivitySourcesName,
                ],
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 11,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivityDataControllers, level: PermissionLevel.Read },
                subPermissions: [
                    PermissionType.ProcessingActivityDataControllersDataProcessorAgreement,
                    PermissionType.ProcessingActivityDataControllersResponsible,
                    PermissionType.ProcessingActivityDataControllersSubProcessors,
                    PermissionType.ProcessingActivityDataControllersTiaAndTransferBasis,
                    PermissionType.ProcessingActivityDataControllersDataCategories,
                    PermissionType.ProcessingActivityDataControllersName,
                ],
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 12,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivityJointDataControllers, level: PermissionLevel.Read },
                subPermissions: [
                    PermissionType.ProcessingActivityJointDataControllersAgreement,
                    PermissionType.ProcessingActivityJointDataControllersResponsible,
                    PermissionType.ProcessingActivityJointDataControllersDataCategories,
                    PermissionType.ProcessingActivityJointDataControllersName,
                ],
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 13,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.Record301, level: null },
                subPermissions: undefined,
                type: PermissionListType.Record,
                accessLevel: [],
                order: 1,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.Record302, level: null },
                subPermissions: undefined,
                type: PermissionListType.Record,
                accessLevel: [],
                order: 2,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.SystemFullAccess, level: null },
                subPermissions: undefined,
                type: PermissionListType.Systems,
                accessLevel: [],
                order: 0,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.SystemManagement, level: PermissionLevel.Delete },
                subPermissions: [PermissionType.StandardTemplateSystemManagement],
                type: PermissionListType.Systems,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit, PermissionLevel.Create, PermissionLevel.Delete],
                order: 1,
                associatedPermissions: [PermissionType.SystemFullAccess],
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.CustomListManagement, level: PermissionLevel.Read },
                subPermissions: undefined,
                type: PermissionListType.Lists,
                accessLevel: getCustomListsAccessLevels(),
                order: 1,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.CompanyDocumentationManagement, level: PermissionLevel.Edit },
                subPermissions: undefined,
                type: PermissionListType.LegalEntities,
                accessLevel: [PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 3,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.PolicyManagement, level: PermissionLevel.Read },
                subPermissions: undefined,
                type: PermissionListType.Policies,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 1,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.ProcessingActivityPolicies, level: PermissionLevel.Read },
                subPermissions: undefined,
                type: PermissionListType.ProcessingActivity,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
                order: 13,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.DocumentStatus, level: null },
                subPermissions: undefined,
                type: PermissionListType.LegalEntities,
                accessLevel: [],
                order: 4,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.DeleteDocumentVersion, level: null },
                subPermissions: undefined,
                type: PermissionListType.LegalEntities,
                accessLevel: [],
                order: 5,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
            {
                masterPermission: { type: PermissionType.CreateLegalEntityUnderTransfers, level: null },
                subPermissions: undefined,
                type: PermissionListType.LegalEntities,
                accessLevel: [],
                order: 6,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            },
        ];

        content.push({
            masterPermission: { type: PermissionType.RiskManagement, level: PermissionLevel.Read },
            subPermissions: undefined,
            type: PermissionListType.Risk,
            accessLevel: [PermissionLevel.Read, PermissionLevel.Edit],
            order: 1,
            associatedPermissions: [PermissionType.RiskTemplateManagement],
            disableAccessLevels: false,
        });

        content.push({
            masterPermission: { type: PermissionType.RiskTemplateManagement, level: PermissionLevel.Read },
            subPermissions: undefined,
            type: PermissionListType.Risk,
            accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
            order: 2,
            associatedPermissions: undefined,
            disableAccessLevels: false,
        });

        content.push({
            masterPermission: { type: PermissionType.RiskVersionManagement, level: PermissionLevel.Read },
            subPermissions: undefined,
            type: PermissionListType.Risk,
            accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
            order: 3,
            associatedPermissions: undefined,
            disableAccessLevels: false,
        });

        if (addonsFeatures.enterpriseRiskManagement) {
            content.push({
                masterPermission: { type: PermissionType.EnterpriseRiskManagement, level: null },
                subPermissions: undefined,
                type: PermissionListType.Risk,
                accessLevel: [],
                order: 4,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            });
        }

        if (platformFeatures.hasTaskManagementAccess) {
            content.push({
                masterPermission: { type: PermissionType.AnnualWheelManagement, level: PermissionLevel.Read },
                subPermissions: undefined,
                type: PermissionListType.AnnualWheel,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 1,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            });

            content.push({
                masterPermission: { type: PermissionType.TaskManagement, level: PermissionLevel.Read },
                subPermissions: [PermissionType.DefaultNotificationReceiver, PermissionType.TaskMasterDataManagement],
                type: PermissionListType.TaskManagement,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 1,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            });
        }

        if (platformFeatures.auditLog)
            content.push({
                masterPermission: { type: PermissionType.CanAccessAuditLog, level: null },
                subPermissions: undefined,
                type: PermissionListType.AuditLog,
                accessLevel: [],
                order: 14,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            });

        content.push({
            masterPermission: { type: PermissionType.IncidentLogFullAccess, level: null },
            subPermissions: undefined,
            type: PermissionListType.IncidentLog,
            accessLevel: [],
            order: 0,
            associatedPermissions: undefined,
            disableAccessLevels: false,
        });

        content.push({
            masterPermission: { type: PermissionType.IncidentLog, level: PermissionLevel.Read },
            subPermissions: undefined,
            type: PermissionListType.IncidentLog,
            accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
            order: 1,
            associatedPermissions: [PermissionType.IncidentLogFullAccess],
            disableAccessLevels: false,
        });

        if (featureFlags.isEnabled(FeatureFlag.ProcessTemplates)) {
            content.push({
                masterPermission: { type: PermissionType.ProcessFullAccess, level: null },
                subPermissions: undefined,
                type: PermissionListType.Processes,
                accessLevel: [],
                order: 0,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            });

            content.push({
                masterPermission: { type: PermissionType.Process, level: PermissionLevel.Read },
                subPermissions: undefined,
                type: PermissionListType.Processes,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 1,
                associatedPermissions: [PermissionType.ProcessFullAccess],
                disableAccessLevels: false,
            });
        }

        content.push({
            masterPermission: { type: PermissionType.Observations, level: PermissionLevel.Read },
            subPermissions: undefined,
            type: PermissionListType.Other,
            accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
            order: 4,
            associatedPermissions: undefined,
            disableAccessLevels: false,
        });

        if (vendorManagementFeatures.hasVendorManagement) {
            content.push({
                masterPermission: { type: PermissionType.InternalAssessment, level: PermissionLevel.Delete },
                subPermissions: undefined,
                type: PermissionListType.VendorManagement,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 1,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            });
            content.push({
                masterPermission: { type: PermissionType.AuditGroups, level: PermissionLevel.Delete },
                subPermissions: undefined,
                type: PermissionListType.VendorManagement,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 2,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            });
        }

        content.push({
            masterPermission: { type: PermissionType.FrameworkManagement, level: PermissionLevel.Read },
            subPermissions: undefined,
            type: PermissionListType.Frameworks,
            accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
            order: 1,
            associatedPermissions: [
                /*PermissionType.FrameworkAuditorApproval*/
            ],
            disableAccessLevels: false,
        });

        // content.push({
        //     masterPermission: { type: PermissionType.FrameworkAuditorApproval, level: null },
        //     subPermissions: undefined,
        //     type: PermissionListType.Frameworks,
        //     accessLevel: [],
        //     order: 2,
        //     associatedPermissions: undefined,
        //     disableAccessLevels: false,
        // });

        if (platformFeatures.tags) {
            content.push({
                masterPermission: { type: PermissionType.Tags, level: null },
                subPermissions: undefined,
                type: PermissionListType.Other,
                accessLevel: [],
                order: 1,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            });
        }

        if (contractManagementFeatures.hasContractManagement) {
            content.push({
                masterPermission: { type: PermissionType.ContractFullAccess, level: null },
                subPermissions: undefined,
                type: PermissionListType.Contracts,
                accessLevel: [],
                order: 0,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            });

            content.push({
                masterPermission: { type: PermissionType.ContractsOverview, level: null },
                subPermissions: [PermissionType.ContractAccessAllContractsWithoutAuthFilter],
                type: PermissionListType.Contracts,
                accessLevel: [],
                order: 1,
                associatedPermissions: [PermissionType.Contracts, PermissionType.ContractFullAccess],
                disableAccessLevels: false,
            });

            content.push({
                masterPermission: { type: PermissionType.Contracts, level: PermissionLevel.Read },
                subPermissions: undefined,
                type: PermissionListType.Contracts,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 2,
                associatedPermissions: [PermissionType.ContractFullAccess],
                disableAccessLevels: false,
            });
        }

        if (addonsFeatures.searchService) {
            content.push({
                masterPermission: { type: PermissionType.GlobalDocumentSearch, level: null },
                subPermissions: undefined,
                type: PermissionListType.Settings,
                accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
                order: 6,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            });
        }

        if (addonsFeatures.aiFeatures) {
            content.push({
                masterPermission: { type: PermissionType.AiFeature, level: null },
                subPermissions: undefined,
                type: PermissionListType.Other,
                accessLevel: [],
                order: 2,
                associatedPermissions: undefined,
                disableAccessLevels: false,
            });
        }

        // Creating global views includes seeing them, so choosing it selects and locks the global views permission.
        content.push({
            masterPermission: { type: PermissionType.GlobalSavedViews, level: null },
            subPermissions: undefined,
            type: PermissionListType.SavedViews,
            accessLevel: [],
            order: 0,
            associatedPermissions: [PermissionType.ManageGlobalViews],
            disableAccessLevels: false,
        });

        content.push({
            masterPermission: { type: PermissionType.StandardSavedViews, level: null },
            subPermissions: undefined,
            type: PermissionListType.SavedViews,
            accessLevel: [],
            order: 1,
            associatedPermissions: undefined,
            disableAccessLevels: false,
        });

        content.push({
            masterPermission: { type: PermissionType.ManageGlobalViews, level: null },
            subPermissions: undefined,
            type: PermissionListType.SavedViews,
            accessLevel: [],
            order: 2,
            associatedPermissions: undefined,
            disableAccessLevels: false,
        });

        content.push({
            masterPermission: { type: PermissionType.AiAdministration, level: PermissionLevel.Delete },
            subPermissions: undefined,
            type: PermissionListType.Settings,
            accessLevel: [PermissionLevel.Read, PermissionLevel.Create, PermissionLevel.Edit, PermissionLevel.Delete],
            order: 6,
            associatedPermissions: undefined,
            disableAccessLevels: true,
        });

        if (addonsFeatures.publicApi) {
            content.push({
                masterPermission: { type: PermissionType.ApiKeyManagement, level: null },
                subPermissions: undefined,
                type: PermissionListType.Settings,
                accessLevel: [],
                order: 8,
                associatedPermissions: undefined,
                disableAccessLevels: true,
            });
        }

        return content;
    }

    function getCustomListsAccessLevels() {
        const accessLevels = [PermissionLevel.Read];

        if (addonsFeatures.customLists) {
            accessLevels.push(PermissionLevel.Create);
            accessLevels.push(PermissionLevel.Edit);
            accessLevels.push(PermissionLevel.Delete);
        }

        return accessLevels;
    }

    function getProcessingActivitySubPermissions() {
        let subPermissions = [
            PermissionType.CanValidateProcessingActivities,
            PermissionType.StandardTemplateProcessingActivityManagement,
            PermissionType.ProcessingActivityStatus,
            PermissionType.ProcessingActivtyNote,
            PermissionType.ProcessingActivityCopy,
            PermissionType.ProcessingActivityDocuments,
            PermissionType.Transfers,
        ];

        if (platformFeatures.commentModule) {
            subPermissions.push(PermissionType.ProcessingActivityComments);
        }

        if (addonsFeatures.research) {
            subPermissions.push(PermissionType.ProcessingActivityDraftStatusManagement);
            subPermissions.push(PermissionType.ProcessingActivityStatistic);
        }

        return subPermissions;
    }

    function subPermissionsThatShouldNotBeIncludedWhenPermissionIsSelected(permissionType: PermissionType) {
        return (
            permissionType !== PermissionType.ProcessingActivityDataSubjectNumberOfProcessorsOnlyCustomValue &&
            permissionType !== PermissionType.ProcessingActivityDataSubjectNumberOfDataSubjectsOnlyCustomValue &&
            permissionType !== PermissionType.ContractsOverview &&
            permissionType !== PermissionType.ContractAccessAllContractsWithoutAuthFilter
        );
    }
}

export function shouldShowSubPermissionTooltip(permissionType: PermissionType) {
    return permissionType === PermissionType.ContractAccessAllContractsWithoutAuthFilter;
}

export function isFullEntityAccessPermission(permissionType: PermissionType) {
    return (
        permissionType === PermissionType.ContractFullAccess ||
        permissionType === PermissionType.SystemFullAccess ||
        permissionType === PermissionType.IncidentLogFullAccess ||
        permissionType === PermissionType.ProcessFullAccess
    );
}

// Mirrors Role.GetPermissionsImpliedByFullAccess. The server stays authoritative and a refetch reconciles this;
// the list exists only so the dialog reflects the grant on click instead of after that round trip.
export function getPermissionsImpliedByFullEntityAccess(permissionType: PermissionType): Array<Permission> {
    switch (permissionType) {
        case PermissionType.ContractFullAccess:
            return [
                { type: PermissionType.ContractsOverview, level: null },
                { type: PermissionType.Contracts, level: PermissionLevel.Delete },
                { type: PermissionType.ContractAccessAllContractsWithoutAuthFilter, level: null },
            ];
        case PermissionType.SystemFullAccess:
            return [{ type: PermissionType.SystemManagement, level: PermissionLevel.Delete }];
        case PermissionType.IncidentLogFullAccess:
            return [{ type: PermissionType.IncidentLog, level: PermissionLevel.Delete }];
        case PermissionType.ProcessFullAccess:
            return [{ type: PermissionType.Process, level: PermissionLevel.Delete }];
        default:
            return [];
    }
}
